import JSZip from "jszip";

export interface WhatsAppMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: Date;
  isSent: boolean;
  isSystemMessage?: boolean;
  attachment?: {
    type: "image" | "audio" | "document" | "video" | "contact";
    url: string;
    fileName?: string;
    aiDescription?: string;
    transcription?: string;
    contactData?: {
      name?: string;
      phone?: string[];
      email?: string[];
      organization?: string;
    };
  };
}

export interface ParsedChat {
  messages: WhatsAppMessage[];
  stats: {
    totalMessages: number;
    imageCount: number;
    audioCount: number;
    documentCount: number;
    videoCount: number;
    contactCount: number;
  };
  mediaFiles: Map<string, Blob>;
}

// Improved regex from whatsapp-chat-parser library
// Handles various date/time formats and LTR/RTL Unicode characters
const sharedRegex =
  /^(?:\u200E|\u200F)*\[?(\d{1,4}[-/.]\s?\d{1,4}[-/.]\s?\d{1,4})[,.]?\s\D*?(\d{1,2}[.:]\d{1,2}(?:[.:]\d{1,2})?)(?:\s([ap]\.?\s?m\.?))?\]?(?:\s-|:)?\s/;
const authorAndMessageRegex = /(.+?):\s([^]*)/;
const messageRegexSystem = /([^]+)/;

const regexParser = new RegExp(
  sharedRegex.source + authorAndMessageRegex.source,
  'i',
);
const regexParserSystem = new RegExp(
  sharedRegex.source + messageRegexSystem.source,
  'i',
);

// Helper functions for date/time parsing
function orderDateComponents(date: string): string[] {
  return date.split(/[-/.]\s?/);
}

function normalizeDate(year: string, month: string, day: string): [string, string, string] {
  const yearNum = year.length === 2 ? `20${year}` : year;
  const monthNum = month.padStart(2, '0');
  const dayNum = day.padStart(2, '0');
  return [yearNum, monthNum, dayNum];
}

function convertTime12to24(time: string, ampm: string): string {
  let [hours, minutes, seconds] = time.split(/[:.]/);
  if (hours === '12') hours = '00';
  if (ampm === 'PM') hours = String(parseInt(hours, 10) + 12);
  return `${hours}:${minutes}${seconds ? `:${seconds}` : ''}`;
}

function normalizeTime(time: string): string {
  const [hours, minutes, seconds] = time.split(/[:.]/);
  return `${hours.padStart(2, '0')}:${minutes}:${seconds || '00'}`;
}

function normalizeAMPM(ampm: string): string {
  return ampm.replace(/[^apm]/gi, '').toUpperCase();
}

function daysBeforeMonths(numericDates: number[][]): boolean {
  // Check if any first component is above 12 (must be day)
  const daysFirst = numericDates.some(date => date[0] > 12);
  if (daysFirst) return true;
  
  // Check if any second component is above 12 (must be day, so days are second)
  const daysSecond = numericDates.some(date => date[1] > 12);
  if (daysSecond) return false;
  
  // Default to true (day/month/year format)
  return true;
}

function parseDate(dateStr: string, timeStr: string, ampm: string | null, daysFirst: boolean): Date {
  const splitDate = orderDateComponents(dateStr);
  let day: string, month: string, year: string;
  
  if (!daysFirst) {
    [month, day, year] = splitDate;
  } else {
    [day, month, year] = splitDate;
  }
  
  [year, month, day] = normalizeDate(year, month, day);
  
  const normalizedTime = ampm ? convertTime12to24(timeStr, normalizeAMPM(ampm)) : timeStr;
  const [hours, minutes, seconds] = normalizeTime(normalizedTime).split(/[:.]/);
  
  return new Date(+year, +month - 1, +day, +hours, +minutes, +seconds);
}

function detectAttachment(text: string): { type: "image" | "audio" | "document" | "video" | "contact", fileName: string } | null {
  // Check for common attachment indicators
  if (text.includes('<attached:') || text.includes('(file attached)')) {
    // Extract filename - remove <attached: prefix and any whitespace
    const fileNameMatch = text.match(/(?:<attached:\s*)?([^\s<>]+\.(jpg|jpeg|png|gif|webp|mp4|avi|mov|wmv|flv|mkv|mp3|wav|m4a|ogg|opus|aac|flac|pdf|doc|docx|txt|xls|xlsx|ppt|pptx|csv|rtf|odt|vcf))/i);
    if (fileNameMatch) {
      const fileName = fileNameMatch[1]; // Get the filename without <attached: prefix
      const ext = fileNameMatch[2].toLowerCase();
      
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
        return { type: 'image', fileName };
      } else if (['mp3', 'wav', 'm4a', 'ogg', 'opus', 'aac', 'flac'].includes(ext)) {
        return { type: 'audio', fileName };
      } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpeg', 'mpg'].includes(ext)) {
        return { type: 'video', fileName };
      } else if (ext === 'vcf') {
        return { type: 'contact', fileName };
      } else {
        return { type: 'document', fileName };
      }
    }
  }
  
  // Direct file reference - supports Unicode characters like Hebrew
  const directMatch = text.match(/(.+?)\.(jpg|jpeg|png|gif|webp|mp4|avi|mov|wmv|flv|mkv|mp3|wav|m4a|ogg|opus|aac|flac|pdf|doc|docx|txt|xls|xlsx|ppt|pptx|csv|rtf|odt|vcf)/i);
  if (directMatch) {
    const fileName = directMatch[0];
    const ext = directMatch[2].toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
      return { type: 'image', fileName };
    } else if (['mp3', 'wav', 'm4a', 'ogg', 'opus', 'aac', 'flac'].includes(ext)) {
      return { type: 'audio', fileName };
    } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpeg', 'mpg'].includes(ext)) {
      return { type: 'video', fileName };
    } else if (ext === 'vcf') {
      return { type: 'contact', fileName };
    } else {
      return { type: 'document', fileName };
    }
  }
  
  return null;
}

export async function parseWhatsAppZip(
  file: File, 
  onProgress?: (progress: number, stage: string) => void
): Promise<ParsedChat> {
  console.log('📦 Starting WhatsApp ZIP parsing...', file.name);
  onProgress?.(10, 'Loading ZIP file...');
  
  const zip = new JSZip();
  const contents = await zip.loadAsync(file);
  console.log('✅ ZIP loaded successfully');
  onProgress?.(20, 'Finding chat file...');
  
  // Find the chat text file
  let chatFile: JSZip.JSZipObject | null = null;
  contents.forEach((relativePath, file) => {
    if (!file.dir && (relativePath.endsWith('_chat.txt') || relativePath.endsWith('chat.txt'))) {
      chatFile = file;
    }
  });

  if (!chatFile) {
    console.error('❌ No chat text file found in ZIP');
    throw new Error('No chat text file found in ZIP');
  }

  console.log('📄 Found chat file:', chatFile.name);
  onProgress?.(30, 'Reading chat content...');
  
  let chatText = await chatFile.async('text');
  console.log('📝 Chat text length:', chatText.length, 'characters');

  // Remove invisible Left-to-Right Mark (U+200E) characters
  const cleanedText = chatText.replace(/\u200E/g, '');
  console.log('🧹 Removed U+200E characters:', chatText.length - cleanedText.length);
  chatText = cleanedText;
  
  onProgress?.(40, 'Extracting media files...');
  const lines = chatText.split('\n');
  console.log('📋 Total lines to parse:', lines.length);
  
  const messages: WhatsAppMessage[] = [];
  const mediaFiles = new Map<string, Blob>();
  let currentMessage: WhatsAppMessage | null = null;
  let imageCount = 0;
  let audioCount = 0;
  let documentCount = 0;
  let videoCount = 0;
  let contactCount = 0;

  // Extract media files
  const mediaPromises: Promise<void>[] = [];
  contents.forEach((relativePath, file) => {
    if (!file.dir && !relativePath.endsWith('_chat.txt') && !relativePath.endsWith('chat.txt')) {
      mediaPromises.push(
        file.async('blob').then(blob => {
          mediaFiles.set(relativePath, blob);
        })
      );
    }
  });
  await Promise.all(mediaPromises);
  console.log('🖼️ Extracted media files:', mediaFiles.size);
  console.log('📁 Media file paths:', Array.from(mediaFiles.keys()));

  // Parse messages with multiline support
  console.log('🔍 Starting message parsing...');
  onProgress?.(60, `Parsing ${lines.length} lines...`);
  
  // First pass: merge multiline messages
  interface RawMessage {
    system: boolean;
    msg: string;
  }
  
  const rawMessages: RawMessage[] = [];
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;
    
    // Check if line matches message pattern
    if (!regexParser.test(trimmedLine)) {
      // Check if it's a system message
      if (regexParserSystem.test(trimmedLine)) {
        rawMessages.push({ system: true, msg: trimmedLine });
      }
      // Otherwise it's part of the previous message (multiline)
      else if (rawMessages.length > 0) {
        const prevMessage = rawMessages.pop()!;
        rawMessages.push({
          system: prevMessage.system,
          msg: `${prevMessage.msg}\n${trimmedLine}`,
        });
      }
    } else {
      rawMessages.push({ system: false, msg: trimmedLine });
    }
  }
  
  console.log(`📝 Merged into ${rawMessages.length} messages`);
  
  // Auto-detect date format (day-first or month-first)
  const parsedForDetection = rawMessages.slice(0, 20).map(obj => {
    if (obj.system) {
      const match = regexParserSystem.exec(obj.msg);
      return match ? match[1] : null;
    }
    const match = regexParser.exec(obj.msg);
    return match ? match[1] : null;
  }).filter(Boolean) as string[];
  
  const numericDates = Array.from(
    new Set(parsedForDetection),
    date => orderDateComponents(date).map(Number)
  );
  
  const daysFirst = daysBeforeMonths(numericDates);
  console.log(`📅 Detected date format: ${daysFirst ? 'day/month/year' : 'month/day/year'}`);
  
  // Second pass: parse structured messages
  for (const rawMsg of rawMessages) {
    const { system, msg } = rawMsg;
    
    // Handle system messages
    if (system) {
      const match = regexParserSystem.exec(msg);
      if (!match) continue;
      
      const [, dateStr, timeStr, ampm, content] = match;
      const timestamp = parseDate(dateStr, timeStr, ampm || null, daysFirst);
      
      messages.push({
        id: `msg-${messages.length}`,
        sender: 'System',
        content,
        timestamp,
        isSent: false,
        isSystemMessage: true,
      });
      continue;
    }
    
    const match = regexParser.exec(msg);
    if (!match) continue;
    
    const [, dateStr, timeStr, ampm, sender, content] = match;
    const timestamp = parseDate(dateStr, timeStr, ampm || null, daysFirst);
    
    // Check for attachments
    const attachment = detectAttachment(content);
    
    if (attachment) {
      console.log('🔍 Found attachment:', attachment.fileName, 'type:', attachment.type);
      // Try to find the actual file in mediaFiles
      let mediaBlob: Blob | undefined;
      let mediaFileName = attachment.fileName;
      
      for (const [path, blob] of mediaFiles.entries()) {
        if (path.includes(attachment.fileName)) {
          mediaBlob = blob;
          mediaFileName = path.split('/').pop() || attachment.fileName;
          console.log('✅ Matched media file:', path);
          break;
        }
      }
      
      if (!mediaBlob) {
        console.log('❌ No media file found for:', attachment.fileName);
      }
      
      if (mediaBlob) {
        const objectUrl = URL.createObjectURL(mediaBlob);
        
        if (attachment.type === 'image') imageCount++;
        else if (attachment.type === 'audio') audioCount++;
        else if (attachment.type === 'video') videoCount++;
        else if (attachment.type === 'contact') contactCount++;
        else documentCount++;
        
        messages.push({
          id: `msg-${messages.length}`,
          sender,
          content: content.replace(attachment.fileName, '').trim() || `Sent ${attachment.type}`,
          timestamp,
          isSent: false, // We'll determine this based on sender names later
          attachment: {
            type: attachment.type,
            url: objectUrl,
            fileName: mediaFileName,
          }
        });
      } else {
        messages.push({
          id: `msg-${messages.length}`,
          sender,
          content,
          timestamp,
          isSent: false,
        });
      }
    } else {
      messages.push({
        id: `msg-${messages.length}`,
        sender,
        content,
        timestamp,
        isSent: false,
      });
    }
  }

  // Determine which messages are sent by the user
  // Find the most common sender (assume it's the user)
  onProgress?.(80, 'Identifying senders...');
  
  const senderCounts = new Map<string, number>();
  messages.forEach(msg => {
    senderCounts.set(msg.sender, (senderCounts.get(msg.sender) || 0) + 1);
  });
  
  let userSender = '';
  let maxCount = 0;
  senderCounts.forEach((count, sender) => {
    if (count > maxCount) {
      maxCount = count;
      userSender = sender;
    }
  });

  // Mark messages as sent/received
  onProgress?.(90, 'Finalizing...');
  
  messages.forEach(msg => {
    msg.isSent = msg.sender === userSender;
  });

  console.log('✨ Parsing complete!');
  console.log('📊 Stats:', {
    totalMessages: messages.length,
    imageCount,
    audioCount,
    documentCount,
    primarySender: userSender,
  });

  onProgress?.(100, 'Complete!');

  return {
    messages,
    stats: {
      totalMessages: messages.length,
      imageCount,
      audioCount,
      documentCount,
      videoCount,
      contactCount,
    },
    mediaFiles,
  };
}
